import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { useSite } from '../../context/SiteContext';
import { categoriesApi, productsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { LogOut, Save, RotateCcw, Home, Package, FileText, Star, BarChart3, Plus, Trash2, FolderTree, Loader2, Lock, BookOpen, Edit, X } from 'lucide-react';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImageUpload from '../../components/admin/ImageUpload';
import SalesSection from '../../components/admin/SalesSection';
import { Heart } from 'lucide-react';

const AdminDashboardPage = () => {
  const { isAuthenticated, logout, changePassword } = useAdmin();
  const { siteData, loading: siteLoading, updateHero, updateSections, updateTestimonials, updateCommitment, updateFeaturedProducts, updateConfig, resetToDefaults, refetchData } = useSite();
  const navigate = useNavigate();

  const [hero, setHero] = useState(siteData.hero);
  const [sections, setSections] = useState(siteData.sections);
  const [testimonials, setTestimonials] = useState(siteData.testimonials);
  const [commitment, setCommitment] = useState(siteData.commitment || { title: 'Our Commitment', content: '' });
  const [featuredProducts, setFeaturedProducts] = useState(siteData.featuredProducts || [
    { productId: '', title: '', description: '' },
    { productId: '', title: '', description: '' }
  ]);
  const [config, setConfig] = useState(siteData.config);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Update local state when siteData is loaded from API
  useEffect(() => {
    if (!siteLoading && siteData) {
      setHero(siteData.hero);
      setSections(siteData.sections);
      setTestimonials(siteData.testimonials);
      setCommitment(siteData.commitment || { title: 'Our Commitment', content: '' });
      setFeaturedProducts(siteData.featuredProducts || [
        { productId: '', title: '', description: '' },
        { productId: '', title: '', description: '' }
      ]);
      setConfig(siteData.config);
    }
  }, [siteLoading, siteData]);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '' });
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    shortName: '',
    slug: '',
    description: '',
    longDescription: '',
    categoryId: '',
    image: '',
    variants: [{ id: '', size: '', price: 0, comparePrice: 0, sku: '', inStock: true }],
    tags: [],
    isNew: false,
    onSale: false,
    faqs: []
  });

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [newBlogPost, setNewBlogPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    headerImage: '',
    author: 'Eden Remedies',
    published: true
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchBlogPosts();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await categoriesApi.getAll();
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await productsApi.getAll();
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog?published_only=false`);
      const data = await res.json();
      setBlogPosts(data);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    }
  };

  const handleCreateBlogPost = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBlogPost)
      });
      if (res.ok) {
        fetchBlogPosts();
        setBlogDialogOpen(false);
        setNewBlogPost({
          title: '',
          excerpt: '',
          content: '',
          headerImage: '',
          author: 'Eden Remedies',
          published: true
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Failed to create blog post:', error);
    }
  };

  const handleUpdateBlogPost = async () => {
    if (!editingBlogPost) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/${editingBlogPost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingBlogPost)
      });
      if (res.ok) {
        fetchBlogPosts();
        setEditingBlogPost(null);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Failed to update blog post:', error);
    }
  };

  const handleDeleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/${postId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchBlogPosts();
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      console.error('Failed to delete blog post:', error);
    }
  };

  const handleSave = async (type) => {
    setSaving(true);
    try {
      switch (type) {
        case 'hero':
          await updateHero(hero);
          break;
        case 'sections':
          await updateSections(sections);
          break;
        case 'testimonials':
          await updateTestimonials(testimonials);
          break;
        case 'commitment':
          await updateCommitment(commitment);
          break;
        case 'featuredProducts':
          await updateFeaturedProducts(featuredProducts);
          break;
        case 'config':
          await updateConfig(config);
          break;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all content to defaults?')) {
      await resetToDefaults();
      window.location.reload();
    }
  };

  // Category handlers
  const handleAddCategory = async () => {
    if (!newCategory.name || !newCategory.slug) return;
    try {
      await categoriesApi.create(newCategory);
      await fetchCategories();
      setNewCategory({ name: '', slug: '' });
      setCategoryDialogOpen(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await categoriesApi.delete(id);
      await fetchCategories();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Product handlers
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_-]+/g, '-').trim();
  };

  const handleProductNameChange = (name) => {
    setNewProduct(prev => ({
      ...prev,
      name,
      slug: generateSlug(name),
      shortName: prev.shortName || name.split('–')[0].trim()
    }));
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.categoryId) {
      alert('Please fill in required fields');
      return;
    }
    try {
      const productData = {
        ...newProduct,
        images: newProduct.image ? [newProduct.image] : [],
        variants: newProduct.variants.map((v, i) => ({
          ...v,
          id: v.id || `variant-${i}`,
          sku: v.sku || `SKU-${Date.now()}-${i}`
        }))
      };
      delete productData.image;
      await productsApi.create(productData);
      await fetchProducts();
      await refetchData();
      setProductDialogOpen(false);
      resetProductForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add product');
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      await productsApi.update(editingProduct.id, editingProduct);
      await fetchProducts();
      await refetchData();
      setEditingProduct(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsApi.delete(id);
      await fetchProducts();
      await refetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const resetProductForm = () => {
    setNewProduct({
      name: '',
      shortName: '',
      slug: '',
      description: '',
      longDescription: '',
      categoryId: '',
      image: '',
      variants: [{ id: '', size: '', price: 0, comparePrice: 0, sku: '', inStock: true }],
      tags: [],
      isNew: false,
      onSale: false,
      faqs: []
    });
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-[#2d4a47] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-serif italic">eden</h1>
              <span className="text-sm opacity-70">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm hover:underline flex items-center gap-1">
                <Home size={16} /> View Site
              </Link>
              <button onClick={handleLogout} className="text-sm hover:underline flex items-center gap-1">
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {saved && (
        <div className="bg-green-500 text-white text-center py-2 text-sm">
          Changes saved successfully!
        </div>
      )}

      {siteLoading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#2d4a47] mr-3" size={24} />
            <span className="text-gray-500">Loading content...</span>
          </div>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-serif">Content Management</h2>
          <Button onClick={handleReset} variant="outline" className="flex items-center gap-2">
            <RotateCcw size={16} /> Reset to Defaults
          </Button>
        </div>

        <Tabs defaultValue="sales" className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 h-auto">
            <TabsTrigger value="sales" className="flex items-center gap-1"><BarChart3 size={14} /> Sales</TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-1"><Package size={14} /> Products</TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-1"><FolderTree size={14} /> Categories</TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-1"><BookOpen size={14} /> Blog</TabsTrigger>
            <TabsTrigger value="hero" className="flex items-center gap-1"><Home size={14} /> Hero</TabsTrigger>
            <TabsTrigger value="sections" className="flex items-center gap-1"><FileText size={14} /> Sections</TabsTrigger>
            <TabsTrigger value="testimonials" className="flex items-center gap-1"><Star size={14} /> Reviews</TabsTrigger>
            <TabsTrigger value="featured" className="flex items-center gap-1"><Package size={14} /> Featured</TabsTrigger>
            <TabsTrigger value="commitment" className="flex items-center gap-1"><Heart size={14} /> Commitment</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Sales Section */}
          <TabsContent value="sales">
            <SalesSection />
          </TabsContent>

          {/* Products Section */}
          <TabsContent value="products" className="bg-white p-6 shadow-sm rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium">Products</h3>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#2d4a47] hover:bg-[#1e3633]">
                    <Plus size={16} className="mr-2" /> Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Product</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Product Name *</Label>
                      <Input
                        value={newProduct.name}
                        onChange={(e) => handleProductNameChange(e.target.value)}
                        placeholder="Timeless Remedy – Anti-Aging Moisturizer"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Short Name</Label>
                        <Input
                          value={newProduct.shortName}
                          onChange={(e) => setNewProduct({ ...newProduct, shortName: e.target.value })}
                          placeholder="Timeless Remedy"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Slug</Label>
                        <Input
                          value={newProduct.slug}
                          onChange={(e) => setNewProduct({ ...newProduct, slug: e.target.value })}
                          placeholder="timeless-remedy"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Category *</Label>
                      <Select value={newProduct.categoryId} onValueChange={(v) => setNewProduct({ ...newProduct, categoryId: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Short Description</Label>
                      <Textarea
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Detailed Description</Label>
                      <RichTextEditor
                        value={newProduct.longDescription}
                        onChange={(v) => setNewProduct({ ...newProduct, longDescription: v })}
                      />
                    </div>
                    <ImageUpload
                      label="Product Image"
                      value={newProduct.image}
                      onChange={(url) => setNewProduct({ ...newProduct, image: url })}
                    />
                    <div>
                      <Label className="mb-2 block">Variants</Label>
                      {newProduct.variants.map((variant, index) => (
                        <div key={index} className="grid grid-cols-5 gap-2 mb-2">
                          <Input
                            placeholder="Size (e.g., 2oz)"
                            value={variant.size}
                            onChange={(e) => {
                              const updated = [...newProduct.variants];
                              updated[index].size = e.target.value;
                              setNewProduct({ ...newProduct, variants: updated });
                            }}
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={variant.price || ''}
                            onChange={(e) => {
                              const updated = [...newProduct.variants];
                              updated[index].price = parseFloat(e.target.value) || 0;
                              setNewProduct({ ...newProduct, variants: updated });
                            }}
                          />
                          <Input
                            type="number"
                            placeholder="Compare Price"
                            value={variant.comparePrice || ''}
                            onChange={(e) => {
                              const updated = [...newProduct.variants];
                              updated[index].comparePrice = parseFloat(e.target.value) || 0;
                              setNewProduct({ ...newProduct, variants: updated });
                            }}
                          />
                          <Input
                            placeholder="SKU"
                            value={variant.sku}
                            onChange={(e) => {
                              const updated = [...newProduct.variants];
                              updated[index].sku = e.target.value;
                              setNewProduct({ ...newProduct, variants: updated });
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const updated = newProduct.variants.filter((_, i) => i !== index);
                              setNewProduct({ ...newProduct, variants: updated.length ? updated : [{ id: '', size: '', price: 0, comparePrice: 0, sku: '', inStock: true }] });
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setNewProduct({
                          ...newProduct,
                          variants: [...newProduct.variants, { id: '', size: '', price: 0, comparePrice: 0, sku: '', inStock: true }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add Variant
                      </Button>
                    </div>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newProduct.isNew}
                          onChange={(e) => setNewProduct({ ...newProduct, isNew: e.target.checked })}
                        />
                        <span className="text-sm">New Arrival</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newProduct.onSale}
                          onChange={(e) => setNewProduct({ ...newProduct, onSale: e.target.checked })}
                        />
                        <span className="text-sm">On Sale</span>
                      </label>
                    </div>
                    <Button onClick={handleAddProduct} className="w-full bg-[#2d4a47] hover:bg-[#1e3633]">
                      Create Product
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {products.map((product) => (
                <div key={product.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {product.images?.[0] && (
                        <img src={product.images[0]} alt={product.name} className="w-20 h-20 object-cover rounded" />
                      )}
                      <div>
                        <h4 className="font-medium">{product.name}</h4>
                        <p className="text-sm text-gray-500">{product.category}</p>
                        <p className="text-sm mt-1">
                          {product.variants?.map(v => `${v.size}: $${v.price}`).join(' | ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingProduct(product)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {editingProduct?.id === product.id && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={editingProduct.name}
                            onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select value={editingProduct.categoryId} onValueChange={(v) => setEditingProduct({ ...editingProduct, categoryId: v })}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={editingProduct.description}
                          onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Multiple Product Images */}
                      <div>
                        <Label className="mb-2 block">Product Images (up to 4)</Label>
                        <p className="text-xs text-gray-500 mb-3">First image will be the main product image shown in listings.</p>
                        <div className="grid grid-cols-2 gap-4">
                          {[0, 1, 2, 3].map((index) => (
                            <div key={index} className="relative">
                              <ImageUpload
                                label={index === 0 ? "Main Image" : `Image ${index + 1}`}
                                value={editingProduct.images?.[index] || ''}
                                onChange={(url) => {
                                  const currentImages = [...(editingProduct.images || [])];
                                  if (url) {
                                    currentImages[index] = url;
                                  } else {
                                    currentImages.splice(index, 1);
                                  }
                                  // Remove empty slots from the end
                                  while (currentImages.length > 0 && !currentImages[currentImages.length - 1]) {
                                    currentImages.pop();
                                  }
                                  setEditingProduct({ 
                                    ...editingProduct, 
                                    images: currentImages,
                                    imageUrl: currentImages[0] || ''
                                  });
                                }}
                              />
                              {index > 0 && editingProduct.images?.[index] && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute top-0 right-0 h-6 w-6 p-0 text-red-500 hover:text-red-700 bg-white rounded-full shadow"
                                  onClick={() => {
                                    const currentImages = [...(editingProduct.images || [])];
                                    currentImages.splice(index, 1);
                                    setEditingProduct({ 
                                      ...editingProduct, 
                                      images: currentImages,
                                      imageUrl: currentImages[0] || ''
                                    });
                                  }}
                                >
                                  <X size={14} />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label>Detailed Description</Label>
                        <RichTextEditor
                          value={editingProduct.longDescription}
                          onChange={(v) => setEditingProduct({ ...editingProduct, longDescription: v })}
                        />
                      </div>
                      
                      {/* Variants Section */}
                      <div>
                        <Label className="mb-2 block">Product Variants (Size & Pricing)</Label>
                        <div className="space-y-2">
                          {(editingProduct.variants || []).map((variant, index) => (
                            <div key={index} className="grid grid-cols-5 gap-2 items-center bg-gray-50 p-2 rounded">
                              <Input
                                placeholder="Size (e.g., 2oz)"
                                value={variant.size || ''}
                                onChange={(e) => {
                                  const updated = [...(editingProduct.variants || [])];
                                  updated[index] = { ...updated[index], size: e.target.value };
                                  setEditingProduct({ ...editingProduct, variants: updated });
                                }}
                              />
                              <div>
                                <Input
                                  type="number"
                                  placeholder="Price"
                                  value={variant.price || ''}
                                  onChange={(e) => {
                                    const updated = [...(editingProduct.variants || [])];
                                    updated[index] = { ...updated[index], price: parseFloat(e.target.value) || 0 };
                                    setEditingProduct({ ...editingProduct, variants: updated });
                                  }}
                                />
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  placeholder="Compare Price"
                                  value={variant.comparePrice || ''}
                                  onChange={(e) => {
                                    const updated = [...(editingProduct.variants || [])];
                                    updated[index] = { ...updated[index], comparePrice: parseFloat(e.target.value) || 0 };
                                    setEditingProduct({ ...editingProduct, variants: updated });
                                  }}
                                />
                              </div>
                              <Input
                                placeholder="SKU"
                                value={variant.sku || ''}
                                onChange={(e) => {
                                  const updated = [...(editingProduct.variants || [])];
                                  updated[index] = { ...updated[index], sku: e.target.value };
                                  setEditingProduct({ ...editingProduct, variants: updated });
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const updated = (editingProduct.variants || []).filter((_, i) => i !== index);
                                  setEditingProduct({ 
                                    ...editingProduct, 
                                    variants: updated.length ? updated : [{ id: `variant-0`, size: '', price: 0, comparePrice: 0, sku: '', inStock: true }] 
                                  });
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          ))}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            const currentVariants = editingProduct.variants || [];
                            setEditingProduct({
                              ...editingProduct,
                              variants: [...currentVariants, { 
                                id: `variant-${currentVariants.length}`, 
                                size: '', 
                                price: 0, 
                                comparePrice: 0, 
                                sku: '', 
                                inStock: true 
                              }]
                            });
                          }}
                        >
                          <Plus size={14} className="mr-1" /> Add Variant
                        </Button>
                      </div>

                      {/* FAQ Section */}
                      <div className="border-t pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-base font-medium">Product FAQs (up to 5)</Label>
                          {(editingProduct.faqs || []).length < 5 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentFaqs = editingProduct.faqs || [];
                                setEditingProduct({
                                  ...editingProduct,
                                  faqs: [...currentFaqs, { question: '', answer: '' }]
                                });
                              }}
                            >
                              <Plus size={14} className="mr-1" /> Add FAQ
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-3">FAQs will appear on the product page and as FAQ schema for SEO.</p>
                        {(editingProduct.faqs || []).map((faq, index) => (
                          <div key={index} className="border rounded-lg p-3 mb-3 bg-gray-50">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-xs font-medium text-gray-500">FAQ {index + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  const updated = (editingProduct.faqs || []).filter((_, i) => i !== index);
                                  setEditingProduct({ ...editingProduct, faqs: updated });
                                }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                            <div className="space-y-2">
                              <Input
                                placeholder="Question (e.g., What is Bakuchiol?)"
                                value={faq.question}
                                onChange={(e) => {
                                  const updated = [...(editingProduct.faqs || [])];
                                  updated[index] = { ...updated[index], question: e.target.value };
                                  setEditingProduct({ ...editingProduct, faqs: updated });
                                }}
                              />
                              <Textarea
                                placeholder="Answer"
                                value={faq.answer}
                                rows={3}
                                onChange={(e) => {
                                  const updated = [...(editingProduct.faqs || [])];
                                  updated[index] = { ...updated[index], answer: e.target.value };
                                  setEditingProduct({ ...editingProduct, faqs: updated });
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        {(editingProduct.faqs || []).length === 0 && (
                          <p className="text-sm text-gray-400 italic">No FAQs added yet. Click "Add FAQ" to create one.</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button onClick={handleUpdateProduct} className="bg-[#2d4a47] hover:bg-[#1e3633]">
                          <Save size={14} className="mr-2" /> Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => setEditingProduct(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Categories Section */}
          <TabsContent value="categories" className="bg-white p-6 shadow-sm rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium">Categories</h3>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#2d4a47] hover:bg-[#1e3633]">
                    <Plus size={16} className="mr-2" /> Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Category</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Category Name</Label>
                      <Input
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({
                          name: e.target.value,
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                        })}
                        placeholder="Moisturizers"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Slug</Label>
                      <Input
                        value={newCategory.slug}
                        onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                        placeholder="moisturizers"
                        className="mt-1"
                      />
                    </div>
                    <Button onClick={handleAddCategory} className="w-full bg-[#2d4a47] hover:bg-[#1e3633]">
                      Add Category
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.id} className="border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">{category.name}</h4>
                    <p className="text-sm text-gray-500">/{category.slug}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Blog Section */}
          <TabsContent value="blog" className="bg-white p-6 shadow-sm rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium">Blog Posts</h3>
              <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#2d4a47] hover:bg-[#1e3633]">
                    <Plus size={14} className="mr-2" /> New Blog Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Blog Post</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={newBlogPost.title}
                        onChange={(e) => setNewBlogPost({ ...newBlogPost, title: e.target.value })}
                        placeholder="Enter blog post title"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Excerpt *</Label>
                      <Textarea
                        value={newBlogPost.excerpt}
                        onChange={(e) => setNewBlogPost({ ...newBlogPost, excerpt: e.target.value })}
                        placeholder="Brief summary shown on blog listing page"
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    <div>
                      <ImageUpload
                        label="Header Image"
                        value={newBlogPost.headerImage}
                        onChange={(url) => setNewBlogPost({ ...newBlogPost, headerImage: url })}
                      />
                    </div>
                    <div>
                      <Label>Content *</Label>
                      <RichTextEditor
                        value={newBlogPost.content}
                        onChange={(v) => setNewBlogPost({ ...newBlogPost, content: v })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Author</Label>
                        <Input
                          value={newBlogPost.author}
                          onChange={(e) => setNewBlogPost({ ...newBlogPost, author: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="newPostPublished"
                          checked={newBlogPost.published}
                          onChange={(e) => setNewBlogPost({ ...newBlogPost, published: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="newPostPublished">Published</Label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setBlogDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleCreateBlogPost} className="bg-[#2d4a47] hover:bg-[#1e3633]">
                        Create Post
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Blog Posts List */}
            <div className="space-y-4">
              {blogPosts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No blog posts yet. Create your first post!</p>
              ) : (
                blogPosts.map((post) => (
                  <div key={post.id} className="border rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      {/* Thumbnail */}
                      <div className="w-24 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        {post.headerImage ? (
                          <img src={post.headerImage} alt={post.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#2d4a47] to-[#4a7c59] flex items-center justify-center">
                            <span className="text-white/50 text-sm">No Image</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900 truncate">{post.title}</h4>
                          {!post.published && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Draft</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2 mb-2">{post.excerpt}</p>
                        <p className="text-xs text-gray-400">
                          By {post.author} • {new Date(post.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingBlogPost(post)}
                        >
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteBlogPost(post.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>

                    {/* Edit Form */}
                    {editingBlogPost?.id === post.id && (
                      <div className="mt-4 pt-4 border-t space-y-4">
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={editingBlogPost.title}
                            onChange={(e) => setEditingBlogPost({ ...editingBlogPost, title: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Excerpt</Label>
                          <Textarea
                            value={editingBlogPost.excerpt}
                            onChange={(e) => setEditingBlogPost({ ...editingBlogPost, excerpt: e.target.value })}
                            className="mt-1"
                            rows={2}
                          />
                        </div>
                        <div>
                          <ImageUpload
                            label="Header Image"
                            value={editingBlogPost.headerImage}
                            onChange={(url) => setEditingBlogPost({ ...editingBlogPost, headerImage: url })}
                          />
                        </div>
                        <div>
                          <Label>Content</Label>
                          <RichTextEditor
                            value={editingBlogPost.content}
                            onChange={(v) => setEditingBlogPost({ ...editingBlogPost, content: v })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Author</Label>
                            <Input
                              value={editingBlogPost.author}
                              onChange={(e) => setEditingBlogPost({ ...editingBlogPost, author: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <input
                              type="checkbox"
                              id={`editPostPublished-${post.id}`}
                              checked={editingBlogPost.published}
                              onChange={(e) => setEditingBlogPost({ ...editingBlogPost, published: e.target.checked })}
                              className="rounded"
                            />
                            <Label htmlFor={`editPostPublished-${post.id}`}>Published</Label>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleUpdateBlogPost} className="bg-[#2d4a47] hover:bg-[#1e3633]">
                            <Save size={14} className="mr-2" /> Save Changes
                          </Button>
                          <Button variant="outline" onClick={() => setEditingBlogPost(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Hero Section */}
          <TabsContent value="hero" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-6">Hero Section</h3>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={hero.title}
                  onChange={(e) => setHero({ ...hero, title: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={hero.description}
                  onChange={(e) => setHero({ ...hero, description: e.target.value })}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div>
                <Label>Button Text</Label>
                <Input
                  value={hero.buttonText}
                  onChange={(e) => setHero({ ...hero, buttonText: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Button Link (Product)</Label>
                <Select 
                  value={hero.buttonLink?.replace('/products/', '') || ''} 
                  onValueChange={(slug) => setHero({ ...hero, buttonLink: `/products/${slug}` })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.slug}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Current link: {hero.buttonLink}</p>
              </div>
              <ImageUpload
                label="Hero Image"
                value={hero.image}
                onChange={(url) => setHero({ ...hero, image: url })}
              />
              <Button onClick={() => handleSave('hero')} className="bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Save Hero
              </Button>
            </div>
          </TabsContent>

          {/* Sections */}
          <TabsContent value="sections" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-6">Content Sections</h3>
            {sections.map((section, index) => (
              <div key={section.id} className="border-b pb-6 mb-6 last:border-0">
                <h4 className="font-medium mb-4">Section {index + 1}</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={section.title}
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[index].title = e.target.value;
                        setSections(updated);
                      }}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={section.description || ''}
                      onChange={(e) => {
                        const updated = [...sections];
                        updated[index].description = e.target.value;
                        setSections(updated);
                      }}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                  <ImageUpload
                    label="Section Image"
                    value={section.image}
                    onChange={(url) => {
                      const updated = [...sections];
                      updated[index].image = url;
                      setSections(updated);
                    }}
                  />
                </div>
              </div>
            ))}
            <Button onClick={() => handleSave('sections')} className="bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Save Sections
            </Button>
          </TabsContent>

          {/* Testimonials */}
          <TabsContent value="testimonials" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-6">Testimonials</h3>
            <div className="space-y-4 mb-6">
              <div>
                <Label>Section Title</Label>
                <Input
                  value={testimonials.title}
                  onChange={(e) => setTestimonials({ ...testimonials, title: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            {testimonials.reviews.map((review, index) => (
              <div key={review.id} className="border-b pb-6 mb-6 last:border-0">
                <h4 className="font-medium mb-4">Review {index + 1}</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Review Text</Label>
                    <Textarea
                      value={review.text}
                      onChange={(e) => {
                        const updated = { ...testimonials };
                        updated.reviews[index].text = e.target.value;
                        setTestimonials(updated);
                      }}
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Author Name</Label>
                      <Input
                        value={review.author}
                        onChange={(e) => {
                          const updated = { ...testimonials };
                          updated.reviews[index].author = e.target.value;
                          setTestimonials(updated);
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Age</Label>
                      <Input
                        type="number"
                        value={review.age}
                        onChange={(e) => {
                          const updated = { ...testimonials };
                          updated.reviews[index].age = parseInt(e.target.value);
                          setTestimonials(updated);
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={() => handleSave('testimonials')} className="bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Save Testimonials
            </Button>
          </TabsContent>

          {/* Featured Products Section */}
          <TabsContent value="featured" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-2">Featured Products</h3>
            <p className="text-sm text-gray-500 mb-6">
              Select which products to feature in the "Product Focus" sections on the homepage. These appear between the info sections.
            </p>
            
            {[0, 1].map((index) => (
              <div key={index} className="border-b pb-6 mb-6 last:border-0">
                <h4 className="font-medium mb-4">Featured Product {index + 1}</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Select Product</Label>
                    <Select
                      value={featuredProducts[index]?.productId || ''}
                      onValueChange={(value) => {
                        const updated = [...featuredProducts];
                        if (!updated[index]) {
                          updated[index] = { productId: '', title: '', description: '' };
                        }
                        updated[index].productId = value;
                        // Auto-fill title and description from product if empty
                        const selectedProduct = products.find(p => p.id === value);
                        if (selectedProduct && !updated[index].title) {
                          updated[index].title = selectedProduct.name;
                        }
                        if (selectedProduct && !updated[index].description) {
                          updated[index].description = selectedProduct.description;
                        }
                        setFeaturedProducts(updated);
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a product..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Custom Title (Optional)</Label>
                    <p className="text-xs text-gray-400 mb-1">Override the product name with a custom headline</p>
                    <Input
                      value={featuredProducts[index]?.title || ''}
                      onChange={(e) => {
                        const updated = [...featuredProducts];
                        if (!updated[index]) {
                          updated[index] = { productId: '', title: '', description: '' };
                        }
                        updated[index].title = e.target.value;
                        setFeaturedProducts(updated);
                      }}
                      placeholder="e.g., The Secret to Radiant Skin"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Custom Description (Optional)</Label>
                    <p className="text-xs text-gray-400 mb-1">Override the product description for this section</p>
                    <Textarea
                      value={featuredProducts[index]?.description || ''}
                      onChange={(e) => {
                        const updated = [...featuredProducts];
                        if (!updated[index]) {
                          updated[index] = { productId: '', title: '', description: '' };
                        }
                        updated[index].description = e.target.value;
                        setFeaturedProducts(updated);
                      }}
                      placeholder="Enter custom description..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <Button onClick={() => handleSave('featuredProducts')} className="bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Save Featured Products
            </Button>
          </TabsContent>

          {/* Commitment Section */}
          <TabsContent value="commitment" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-6">Our Commitment Section</h3>
            <p className="text-sm text-gray-500 mb-6">
              This section appears at the bottom of the homepage (full width) and on product/blog pages (boxed).
            </p>
            <div className="space-y-6">
              <div>
                <Label>Section Title</Label>
                <Input
                  value={commitment.title}
                  onChange={(e) => setCommitment({ ...commitment, title: e.target.value })}
                  placeholder="Our Commitment"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Content</Label>
                <p className="text-xs text-gray-400 mb-2">Use double line breaks (empty lines) to create new paragraphs.</p>
                <Textarea
                  value={commitment.content}
                  onChange={(e) => setCommitment({ ...commitment, content: e.target.value })}
                  placeholder="Enter your commitment message here..."
                  rows={12}
                  className="mt-1"
                />
              </div>
            </div>
            <Button onClick={() => handleSave('commitment')} className="mt-6 bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Save Commitment
            </Button>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="bg-white p-6 shadow-sm rounded-lg">
            <h3 className="text-lg font-medium mb-6">Site Settings</h3>
            <div className="space-y-6">
              <div>
                <Label>Announcement Bar Text</Label>
                <Input
                  value={config.tagline}
                  onChange={(e) => setConfig({ ...config, tagline: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Social Media Links</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Instagram URL</Label>
                    <Input
                      value={config.socialMedia?.instagram || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        socialMedia: { ...config.socialMedia, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/yourprofile"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Facebook URL</Label>
                    <Input
                      value={config.socialMedia?.facebook || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        socialMedia: { ...config.socialMedia, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/yourpage"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Twitter / X URL</Label>
                    <Input
                      value={config.socialMedia?.twitter || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        socialMedia: { ...config.socialMedia, twitter: e.target.value }
                      })}
                      placeholder="https://twitter.com/yourhandle"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Pinterest URL</Label>
                    <Input
                      value={config.socialMedia?.pinterest || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        socialMedia: { ...config.socialMedia, pinterest: e.target.value }
                      })}
                      placeholder="https://pinterest.com/yourprofile"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>YouTube URL</Label>
                    <Input
                      value={config.socialMedia?.youtube || ''}
                      onChange={(e) => setConfig({ 
                        ...config, 
                        socialMedia: { ...config.socialMedia, youtube: e.target.value }
                      })}
                      placeholder="https://youtube.com/@yourchannel"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={() => handleSave('config')} className="bg-[#2d4a47] hover:bg-[#1e3633]" disabled={saving}>
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Save Settings
              </Button>

              {/* Change Password Section */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Lock size={18} /> Change Admin Password
                </h4>
                
                {passwordSuccess && (
                  <div className="bg-green-50 text-green-700 text-sm p-3 rounded mb-4">
                    Password changed successfully!
                  </div>
                )}
                
                {passwordError && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded mb-4">
                    {passwordError}
                  </div>
                )}
                
                <div className="space-y-4 max-w-md">
                  <div>
                    <Label>Current Password</Label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="mt-1"
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    onClick={async () => {
                      setPasswordError('');
                      setPasswordSuccess(false);
                      
                      if (!currentPassword || !newPassword || !confirmPassword) {
                        setPasswordError('Please fill in all fields');
                        return;
                      }
                      
                      if (newPassword !== confirmPassword) {
                        setPasswordError('New passwords do not match');
                        return;
                      }
                      
                      if (newPassword.length < 6) {
                        setPasswordError('Password must be at least 6 characters');
                        return;
                      }
                      
                      setChangingPassword(true);
                      const result = await changePassword(currentPassword, newPassword);
                      setChangingPassword(false);
                      
                      if (result.success) {
                        setPasswordSuccess(true);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setTimeout(() => setPasswordSuccess(false), 3000);
                      } else {
                        setPasswordError(result.error);
                      }
                    }}
                    disabled={changingPassword}
                    variant="outline"
                    className="border-[#2d4a47] text-[#2d4a47] hover:bg-[#2d4a47] hover:text-white"
                  >
                    {changingPassword ? <Loader2 className="animate-spin mr-2" size={16} /> : <Lock size={16} className="mr-2" />}
                    Change Password
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
